export const rotateBase64Image90CW = (base64: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.height;
      canvas.height = img.width;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.9,
      );
    };
    img.onerror = reject;
    img.src = base64;
  });

/** Apply multiple 90° clockwise turns in a single canvas pass (avoids sequential re-encode and matches one save). */
export const rotateBase64ImageByQuarterTurns = (base64: string, quarterTurns: number): Promise<Blob> => {
  const n = ((Math.floor(quarterTurns) % 4) + 4) % 4;
  if (n === 0) {
    return fetch(base64).then(r => {
      if (!r.ok) throw new Error('Failed to read image data');
      return r.blob();
    });
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        const w = img.width;
        const h = img.height;
        if (n === 1 || n === 3) {
          canvas.width = h;
          canvas.height = w;
        } else {
          canvas.width = w;
          canvas.height = h;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((n * Math.PI) / 2);
        ctx.drawImage(img, -w / 2, -h / 2);
        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          0.9,
        );
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64;
  });
};

export interface ProcessedImage {
  base64: string;
  width: number;
  height: number;
  orientation: 'landscape' | 'portrait';
}

interface OptimizeImageForPdfOptions {
  targetWidth: number;
  targetHeight: number;
  maxBase64Length?: number;
  qualitySteps?: number[];
  scaleSteps?: number[];
}

// Convert blob name to base64 using provided API function
export const convertImageToBase64 = async (blobName: string, getImageAsBase64: (blobName: string) => Promise<string>): Promise<string> => {
  try {
    const base64 = await getImageAsBase64(blobName);

    // Validate base64 format
    if (!base64.startsWith('data:image/')) {
      console.warn('Backend returned base64 does not have expected format:', base64.substring(0, 50) + '...');
    }

    return base64;
  } catch (error) {
    console.error('Error converting image to base64:', error);
    throw error;
  }
};

// Convert blob name to base64 with fallback handling
export const convertImageToBase64WithFallback = async (
  blobName: string,
  getImageAsBase64: (blobName: string) => Promise<string>,
): Promise<string> => {
  try {
    return await convertImageToBase64(blobName, getImageAsBase64);
  } catch (error) {
    console.error('Frontend image conversion failed:', error);
    throw error;
  }
};

// Resize image using Canvas API for PDF layout (cover behavior: crop to fill, maintain aspect ratio)
export const resizeImageForPDF = async (base64Image: string, targetWidth: number, targetHeight: number, quality = 0.8): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Cover behavior (same as CSS object-fit: cover): scale image to fill target, center, and crop overflow
        const scale = Math.max(targetWidth / img.width, targetHeight / img.height);
        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;
        const dx = (targetWidth - drawWidth) / 2;
        const dy = (targetHeight - drawHeight) / 2;

        ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, drawWidth, drawHeight);

        const resizedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(resizedBase64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64Image;
  });
};

const canvasToBlob = (canvas: HTMLCanvasElement, type = 'image/jpeg', quality = 0.8): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Failed to create image blob'));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });

export const compressCanvasForUpload = async (sourceCanvas: HTMLCanvasElement, maxDimension = 2000, quality = 0.82): Promise<Blob> => {
  const longestSide = Math.max(sourceCanvas.width, sourceCanvas.height);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;

  if (scale === 1) {
    return canvasToBlob(sourceCanvas, 'image/jpeg', quality);
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sourceCanvas.width * scale));
  canvas.height = Math.max(1, Math.round(sourceCanvas.height * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
  return canvasToBlob(canvas, 'image/jpeg', quality);
};

export const optimizeImageForPdf = async (base64Image: string, options: OptimizeImageForPdfOptions): Promise<string> => {
  const qualitySteps = options.qualitySteps ?? [0.82, 0.72, 0.6];
  const scaleSteps = options.scaleSteps ?? [1, 0.85, 0.7];

  let bestCandidate = base64Image;

  for (const scale of scaleSteps) {
    const targetWidth = Math.max(1, Math.round(options.targetWidth * scale));
    const targetHeight = Math.max(1, Math.round(options.targetHeight * scale));

    for (const quality of qualitySteps) {
      const candidate = await resizeImageForPDF(base64Image, targetWidth, targetHeight, quality);
      bestCandidate = candidate;

      if (!options.maxBase64Length || candidate.length <= options.maxBase64Length) {
        return candidate;
      }
    }
  }

  return bestCandidate;
};

// Create a placeholder image for missing photos
export const createImagePlaceholder = (fileName: string): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return '';
  }

  // Set canvas size (3in x 2in at 150 DPI = 450 x 300 pixels)
  canvas.width = 450;
  canvas.height = 300;

  // Fill with light gray background
  ctx.fillStyle = '#F5F5F5';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Add border
  ctx.strokeStyle = '#CCCCCC';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);

  // Add text
  ctx.fillStyle = '#666666';
  ctx.font = '16px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const lines = ['Image Not Available', fileName.length > 30 ? fileName.substring(0, 30) + '...' : fileName];

  lines.forEach((line, index) => {
    ctx.fillText(line, canvas.width / 2, canvas.height / 2 + (index - 0.5) * 20);
  });

  return canvas.toDataURL('image/jpeg', 0.8);
};

// Get image dimensions from base64
export const getImageDimensions = (base64Image: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = reject;
    img.src = base64Image;
  });
};

// Determine image orientation
export const getImageOrientation = (width: number, height: number): 'landscape' | 'portrait' => {
  return width >= height ? 'landscape' : 'portrait';
};
